import React, { Component } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"

import Alert, { ALERT } from "../../components/shared/Alert"
import ApplicationLayout from "../../layouts/ApplicationLayout"
import LoadingError from "../../components/shared/LoadingError"
import LoadingThrobber from "../../components/shared/LoadingThrobber"
import VehicleList from "../../components/vehicles/VehicleList"
import Stats from "../../components/vehicles/Stats"
import { withTranslation } from "react-i18next"
import AlertModal from "../../components/shared/AlertModal"
import { VEHICLE_INDEX } from "../../constants/application"
import * as sessionHelpers from "../../helpers/sessionHelpers"
import { DRIVER, MANAGER } from "../../constants/roles"
import { isFleetAdvise, isFleetcor } from "../../helpers/affiliationHelpers"
import VehicleIndexShimmer from "./VehicleIndexShimmer"
import { logFirebaseAnalyticsScreenName } from "../../helpers/googleAnalyticsHelpers"
import { getRouteNameFromUrl } from "../../helpers/segmentHelpers"
import { getVehicleClassfiedData } from "../../helpers/vehicleHelpers"
import { DEACTIVE } from "../../constants/vehicles"
import CustomNotification from "../../components/shared/CustomNotification"
import i18n from "../../utilities/i18n"
import { clearStateAfterTimeout } from "../../helpers/documentHelpers"

class VehicleIndex extends Component {
  static propTypes = {
    isLoading: PropTypes.bool.isRequired,
    isLoadingError: PropTypes.bool.isRequired,
    users: PropTypes.array.isRequired,
    vehicles: PropTypes.array.isRequired,
    vehiclesDataType: PropTypes.string.isRequired,
    fleet: PropTypes.object.isRequired
  }

  constructor(props) {
    super(props)

    // NOTE: locationStateData is instantiated as a variable here rather than referencing this.props.location.state.someObj directly because the state key may not be present when navigating to this page from a <Link> that does not explicitly set it.
    const locationStateData = this.props.location.state || {}

    this.state = {
      alert: locationStateData.alert || "",
      alertType: locationStateData.alertType || "default",
      openAlertModal: false,
      vehicleIdToDelete: null,
      vehicleList: [],
      rootCheckbox: false,
      isBulkApiInProgress: false,
      showInactiveVehicles: false,
      filteredVehicles: [],
      loading: true,
      checkedVehicles: new Set()
    }
    this.handleActivateDeactivateRootCheckbox = this.handleActivateDeactivateRootCheckbox.bind(this)
    this.handleCellCheckboxChange = this.handleCellCheckboxChange.bind(this)
  }

  onDeleteVehicle = (vehicleId, t) => {
    const confirmed = window.confirm(t("deleteConfirmLabel"))

    if (confirmed) {
      this.props.dispatch({
        type: "VEHICLE_DELETE_SAGA",
        payload: {
          vehicleId: vehicleId
        },
        callback: this.afterDeleteVehicle.bind(this)
      })
    }
  }

  onDeactivateVehicle = (vehicleStatus, vehicleId, t) => {
    var confirmed
    if (vehicleStatus == true) {
      confirmed = window.confirm(t("deactivateConfirmLabel"))
    } else {
      confirmed = window.confirm(t("activateConfirmLabel"))
    }

    if (confirmed) {
      this.props.dispatch({
        type: "VEHICLE_DEACTIVATE_SAGA",
        payload: {
          vehicleId: vehicleId
        },
        callback: this.afterDeleteVehicle.bind(this)
      })
    }
  }

  handleModalState = () => {
    this.setState({ openAlertModal: false })
  }

  onDeleteVehicleSuccess(data) {
    CustomNotification(data.alertType, i18n.t("settings:successLable"), data.alertMessage, i18n.t)
    this.loadAllVehicles()
  }

  loadAllVehicles = async () => {
    const { id } = this.props.fleet
    const { currentUser } = this.props
    this.setState({
      loading: true
    })
    await this.props.dispatch({
      type: "VEHICLES_LOAD_SAGA",
      payload: {
        options: {
          fleet_id: !currentUser.roles.find((role) => role === MANAGER || role === DRIVER) && id
        }
      },
      callback: () => {
        this.setState({
          loading: false
        })
      }
    })
    this.setState({ rootCheckbox: false, isBulkApiInProgress: false })
  }

  afterDeleteVehicle = (status, data) => {
    if (status === "success") {
      this.onDeleteVehicleSuccess(data)
    } else {
      this.onRequestFailure(data)
    }
  }

  onRequestFailure = (data) => {
    CustomNotification(data.alertType, i18n.t("settings:errorLabel"), data.alertMessage, i18n.t)
  }

  componentDidMount() {
    logFirebaseAnalyticsScreenName(getRouteNameFromUrl())

    this.loadAllVehicles()
  }

  sortOrder = {
    enrolled: 0,
    later: 1,
    spare: 2
  }

  componentDidUpdate(prevProps) {
    if (prevProps.vehicles !== this.props.vehicles) {
      this.setState({
        vehicleList: this.props.vehicles,
        showInactiveVehicles: false,
        filteredVehicles: this.props.vehicles
          .filter((vehicle) => vehicle.is_active)
          .sort((a, b) => {
            const aOrder = this.sortOrder[a.enroll_code] ?? 999 // Default to 999 if enroll_code is missing or invalid
            const bOrder = this.sortOrder[b.enroll_code] ?? 999
            return aOrder - bOrder
          })
          .map((vehicle, index) => {
            vehicle.index = index
            return vehicle
          })
      })
    }
  }

  handleActivateDeactivateRootCheckbox(isChecked) {
    const updatedRowData = this.state.filteredVehicles.map((row) => ({
      ...row,
      checked: isChecked
    }))
    this.setState({ filteredVehicles: updatedRowData, rootCheckbox: isChecked })
  }

  handleCellCheckboxChange = (rowIndex, isChecked) => {
    const updatedRowData = [...this.state.filteredVehicles]
    updatedRowData[rowIndex].checked = isChecked
    const allChecked = updatedRowData.every((row) => row.checked)
    if (!allChecked) {
      this.setState({ rootCheckbox: false })
    }
    this.setState({ filteredVehicles: updatedRowData })
    return updatedRowData.findIndex((vehicle) => vehicle.checked) > -1
  }

  handleVehicleActivation = async (status = DEACTIVE) => {
    const vehicles = this.state.filteredVehicles.filter((vehicle) =>
      [...this.state.checkedVehicles].includes(vehicle.id)
    )
    const { activeVehicle, deactiveVehicle } = getVehicleClassfiedData(vehicles)
    this.setState({ isBulkApiInProgress: true })

    this.props.dispatch({
      type: "BULK_ACTIVATE_DEACTIVATE_VEHICLE",
      payload: {
        vehicleIds:
          status === DEACTIVE
            ? activeVehicle && activeVehicle.map((vehicle) => vehicle.id)
            : deactiveVehicle && deactiveVehicle.map((vehicle) => vehicle.id),
        status: status
      },
      callback: this.loadAllVehicles.bind(this)
    })
  }

  setShowInactiveVehicles = (_, data) => {
    this.setState(
      (prevState) => ({
        showInactiveVehicles: data.checked,
        filteredVehicles: prevState.vehicleList
          .filter((vehicle) => {
            return data.checked ? !vehicle.is_active : vehicle.is_active
          })
          .map((vehicle, index) => {
            vehicle.index = index
            return vehicle
          })
      }),
      () => {
        this.handleActivateDeactivateRootCheckbox(false)
      }
    )
  }

  shouldRenderContent() {
    const { isLoading, isLoadingError } = this.props

    // NOTE: vehiclesDataType check is not required for the current version of the vehicles index. However, if the vehicles data loaded by the dashboard should change in the future and break this view, than it can be enabled.
    // NOTE: Enabling the vehiclesDataType check will cause an issue where if the vehicles index is rendered and the VEHICLES_LOAD_SAGA completes before the DASHBOARD_LOAD_SAGA completes, the DASHBOARD_LOAD_SAGA will continue to run in the background, and wipe out the results on the vehicles index when it completes and sets the vehiclesDataType to "extended".
    // Steps to reproduce:
    // 1. Go to dashboard tab.
    // 2. While dashboard is still loading, click on vehicles tab. You should see index of vehicles.
    // 3. Wait, the vehicles list should disappear (You can watch redux tools for the DASHBOARD_LOAD_SAGA to complete).
    return !isLoading && !isLoadingError && !this.state.loading // && vehiclesDataType === 'standard'
  }

  setCheckedVehicles = (checkedVehicles) => {
    this.setState({
      checkedVehicles: checkedVehicles
    })
  }

  renderContent() {
    const { users, vehicles, fleet, currentUser, language, dispatch, t } = this.props
    const { filteredVehicles, showInactiveVehicles } = this.state

    if (this.shouldRenderContent()) {
      return (
        <div className="vehicle-index-container">
          <Stats fleet={fleet} vehicles={vehicles} language={language} />
          <VehicleList
            users={users}
            vehicles={filteredVehicles}
            onDeleteVehicle={this.onDeleteVehicle}
            onDeactivateVehicle={this.onDeactivateVehicle}
            language={language}
            dispatch={dispatch}
            currentUser={currentUser}
            handleVehicleActivateRootCheckbox={this.handleActivateDeactivateRootCheckbox}
            handleCellCheckboxChange={this.handleCellCheckboxChange}
            activateRootCheckbox={this.state.rootCheckbox}
            handleVehicleActivation={this.handleVehicleActivation}
            isBulkApiInProgress={this.state.isBulkApiInProgress}
            showInactiveVehicles={showInactiveVehicles}
            setShowInactiveVehicles={this.setShowInactiveVehicles}
            checkedVehicles={this.state.checkedVehicles}
            setCheckedVehicles={this.setCheckedVehicles}
          />
        </div>
      )
    } else {
      return isFleetAdvise() ? <VehicleIndexShimmer /> : null
    }
  }

  render() {
    const { isLoading, isLoadingError } = this.props

    const { alert, alertType, openAlertModal, vehicleIdToDelete } = this.state

    return (
      <ApplicationLayout>
        {alert && (
          <Alert
            message={alert}
            type={alertType}
            clearStateAfterTimeout={() => clearStateAfterTimeout(this.setState.bind(this), ALERT)}
          />
        )}

        {!isFleetAdvise() ? <LoadingThrobber visible={isLoading} /> : null}

        <LoadingError visible={!isLoading && isLoadingError} />

        {this.renderContent()}
        {openAlertModal ? (
          <AlertModal
            openAlertModal={openAlertModal}
            hideModal={this.handleModalState}
            idToDelete={vehicleIdToDelete}
            afterDelete={this.afterDeleteVehicle}
            that={this}
            modelFrom={VEHICLE_INDEX}
          />
        ) : null}
      </ApplicationLayout>
    )
  }
} // class VehicleIndex

const mapStateToProps = (state) => ({
  isLoading: state.application.isLoading,
  isLoadingError: state.application.isLoadingError,
  users: state.users.users,
  vehicles: state.vehicles.vehicles,
  vehiclesDataType: state.vehicles.vehiclesDataType,
  fleet: state.application.fleetSessionData || state.fleets.fleet,
  language:
    (state.application.userSessionData && state.application.userSessionData.language) ||
    (state.users.currentUser && state.users.currentUser.language),
  currentUser: state.application.userSessionData || state.users.currentUser
})

export default connect(mapStateToProps)(withTranslation("vehicle")(VehicleIndex))
